import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import Header from './components/Header';
import HomePage from './components/HomePage';
import CategoryPage from './components/CategoryPage';
import SearchResults from './components/SearchResults';
import FavoritePage from './components/FavoritePage';
import { categories, paiPaiTexts } from './data/texts';

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #333;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [userPreferences, setUserPreferences] = useState({
    categories: [],
    recentViews: []
  });

  // 从localStorage加载数据
  useEffect(() => {
    const savedFavorites = localStorage.getItem('paipaipai-favorites');
    const savedPreferences = localStorage.getItem('paipaipai-preferences');

    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }

    if (savedPreferences) {
      setUserPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  // 保存收藏到localStorage
  useEffect(() => {
    localStorage.setItem('paipaipai-favorites', JSON.stringify(favorites));
  }, [favorites]);

  // 保存偏好到localStorage
  useEffect(() => {
    localStorage.setItem('paipaipai-preferences', JSON.stringify(userPreferences));
  }, [userPreferences]);

  // 添加/移除收藏
  const toggleFavorite = (text, category) => {
    const item = { text, category, id: Date.now() + Math.random() };

    setFavorites(prev => {
      const exists = prev.find(fav => fav.text === text);
      if (exists) {
        return prev.filter(fav => fav.text !== text);
      } else {
        return [...prev, item];
      }
    });
  };

  // 检查是否已收藏
  const isFavorite = (text) => {
    return favorites.some(fav => fav.text === text);
  };

  // 记录用户偏好
  const recordPreference = (category) => {
    setUserPreferences(prev => ({
      ...prev,
      categories: [...prev.categories, category].slice(-10), // 只保留最近10个
      recentViews: [...prev.recentViews, Date.now()].slice(-20)
    }));
  };

  // 搜索功能
  const searchTexts = (query) => {
    if (!query.trim()) return [];

    const results = [];
    Object.keys(paiPaiTexts).forEach(category => {
      paiPaiTexts[category].forEach(text => {
        if (text.toLowerCase().includes(query.toLowerCase())) {
          results.push({ text, category });
        }
      });
    });

    return results;
  };

  // 智能推荐 - 使用稳定的随机算法
  const getRecommendations = () => {
    const recommendations = [];
    // 使用用户偏好长度作为种子，保持相对稳定
    const seed = userPreferences.categories.length * 123 + Date.now() % 10000;

    // 基于用户偏好推荐
    if (userPreferences.categories.length > 0) {
      const preferredCategories = [...new Set(userPreferences.categories)];
      preferredCategories.forEach((cat, catIndex) => {
        const texts = paiPaiTexts[cat] || [];
        // 使用固定种子进行排序
        const randomTexts = [...texts]
          .sort((a, b) => {
            const hashA = (seed + catIndex + a.length) % 1000;
            const hashB = (seed + catIndex + b.length) % 1000;
            return hashA - hashB;
          })
          .slice(0, 2);
        randomTexts.forEach(text => {
          recommendations.push({ text, category: cat, reason: '基于你的喜好' });
        });
      });
    }

    // 随机推荐
    const randomCategoryIndex = seed % categories.length;
    const randomCategory = categories[randomCategoryIndex];
    const randomTexts = [...(paiPaiTexts[randomCategory.id] || [])]
      .sort((a, b) => {
        const hashA = (seed + a.length) % 1000;
        const hashB = (seed + b.length) % 1000;
        return hashA - hashB;
      })
      .slice(0, 3);
    randomTexts.forEach(text => {
      if (!recommendations.find(r => r.text === text)) {
        recommendations.push({ text, category: randomCategory.id, reason: '随机推荐' });
      }
    });

    return recommendations.slice(0, 6);
  };

  return (
    <Router>
      <AppContainer>
        <Header
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                categories={categories}
                recommendations={getRecommendations()}
                onToggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                onCategoryClick={recordPreference}
              />
            }
          />
          <Route
            path="/category/:categoryId"
            element={
              <CategoryPage
                categories={categories}
                texts={paiPaiTexts}
                onToggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                onCategoryView={recordPreference}
              />
            }
          />
          <Route
            path="/search"
            element={
              <SearchResults
                searchQuery={searchQuery}
                searchResults={searchTexts(searchQuery)}
                categories={categories}
                onToggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
              />
            }
          />
          <Route
            path="/favorites"
            element={
              <FavoritePage
                favorites={favorites}
                categories={categories}
                onToggleFavorite={toggleFavorite}
              />
            }
          />
        </Routes>
      </AppContainer>
    </Router>
  );
}

export default App;
